import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { theDataPageSchema } from '../components/StructuredData';
import './Features.css';
import './TheData.css';

const TheData: React.FC = () => {
  const [monthlyGuests, setMonthlyGuests] = useState<number>(8000);
  const [captureRate, setCaptureRate] = useState<number>(5);

  const contactsPerMonth = Math.round(monthlyGuests * (captureRate / 100));
  const contactsPerYear = contactsPerMonth * 12;
  const lowEstimate = contactsPerYear * 30;
  const highEstimate = contactsPerYear * 80;

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  }, []);

  const formatNumber = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  }, []);

  return (
    <div className="feature-page">
      <SEO
        title="The Data - TableChat | Guest Contact Value for Restaurants"
        description="Industry-verified data on the value of capturing guest contact information during the meal. See how much annual revenue asset exists in your monthly guest traffic with TableChat's contact value calculator."
        canonical="https://tablechat.io/data"
      />
      <StructuredData data={theDataPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'The Data', url: 'https://tablechat.io/data' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>The Value of Every Guest Contact You Capture</h1>
              <p className="hero-lead">
                Your restaurant already has the traffic. The question is whether you're capturing the 
                guest relationship — or letting it walk out the door. Here's what the industry data says 
                about the value of every contact you collect.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/why-tablechat-guests.png" 
                alt="Guests engaged at a restaurant table — every visit is an opportunity to capture a direct guest relationship" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industry Facts */}
      <section className="feature-stats data-facts-section">
        <div className="container">
          <div className="section-intro" style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 48px' }}>
            <h2 style={{ fontSize: '2.25rem', fontWeight: 600, marginBottom: 14, color: 'rgba(0,0,0,0.87)' }}>
              Industry-Verified Facts
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.6 }}>
              These numbers come from published industry research — not projections, not estimates, 
              not marketing spin.
            </p>
          </div>

          <div className="data-facts-grid">
            <div className="data-fact-card">
              <div className="data-fact-number">12–18%</div>
              <div className="data-fact-label">More annual revenue</div>
              <p className="data-fact-detail">
                Guests whose contact information is captured generate 12–18% more annual revenue 
                than anonymous guests.
              </p>
              <span className="data-fact-source">Source: Deliverect Restaurant Loyalty Report</span>
            </div>

            <div className="data-fact-card">
              <div className="data-fact-number">95%</div>
              <div className="data-fact-label">Of first-time diners never return</div>
              <p className="data-fact-detail">
                Without direct engagement after the visit, 95% of first-time guests never come back. 
                Captured contact information is the only way to reach them.
              </p>
              <span className="data-fact-source">Source: SevenRooms Hospitality Report</span>
            </div>

            <div className="data-fact-card">
              <div className="data-fact-number">98%</div>
              <div className="data-fact-label">SMS open rate</div>
              <p className="data-fact-detail">
                SMS messages achieve a 98% open rate, compared to 37–42% for email. Guest phone 
                numbers are among the most valuable contact data a restaurant can capture.
              </p>
              <span className="data-fact-source">Source: Olo; Emarsys Hospitality Benchmarks</span>
            </div>

            <div className="data-fact-card">
              <div className="data-fact-number">2–6%</div>
              <div className="data-fact-label">Organic social media reach</div>
              <p className="data-fact-detail">
                Organic social media posts typically reach only 2–6% of followers. Most of your guests 
                will never see your restaurant's social posts — direct contact is the only reliable channel.
              </p>
              <span className="data-fact-source">Source: Hootsuite Social Media Benchmark Report; RivalIQ Industry Benchmark Report</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Value Breakdown */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>What a Captured Guest Contact Is Worth</h2>
            <p>
              Based on published restaurant industry benchmarks for email and SMS marketing performance, 
              each captured guest contact creates measurable annual revenue value.
            </p>
          </div>

          <div className="feature-blocks">
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Annual Value Per Captured Contact</h3>
                <p>
                  When a restaurant captures a guest's email or phone number with consent, that contact 
                  becomes a direct marketing channel. The guest can be reached with promotions, event 
                  announcements, seasonal offers, and re-engagement campaigns — creating incremental 
                  revenue that would not exist otherwise.
                </p>
                <p>
                  These are industry-derived estimates based on published benchmarks for restaurant 
                  email and SMS marketing ROI, open rates, and conversion data.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Estimated Annual Value Per Contact</h4>
                <ul>
                  <li>
                    <strong>Email contact:</strong> $8–$25 per guest per year
                    <span className="inline-source">(Based on email ROI of $36 per $1 spent — Olo; Campaign Monitor)</span>
                  </li>
                  <li>
                    <strong>SMS contact:</strong> $20–$60 per guest per year
                    <span className="inline-source">(Based on SMS subscriber LTV benchmarks — Emarsys; Postscript)</span>
                  </li>
                  <li>
                    <strong>Combined conservative estimate:</strong> $30–$80 per captured guest per year
                  </li>
                  <li>This represents incremental annual revenue created by the ability to reach the guest directly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Calculator */}
      <section className="feature-how-it-works data-calculator-section">
        <div className="container">
          <h2>Your Restaurant's Annual Contact Value</h2>
          <p className="calculator-subtitle">
            Enter your numbers to see the annual revenue asset TableChat can help you build.
          </p>

          <div className="calculator-wrapper">
            <div className="calculator-inputs">
              <div className="calculator-field">
                <label htmlFor="monthlyGuests">Monthly Guest Count</label>
                <input
                  id="monthlyGuests"
                  type="number"
                  min={0}
                  max={999999}
                  value={monthlyGuests}
                  onChange={(e) => setMonthlyGuests(Math.max(0, parseInt(e.target.value) || 0))}
                  className="calculator-input"
                />
              </div>

              <div className="calculator-field">
                <label htmlFor="captureRate">
                  Contact Capture Rate: <strong>{captureRate}%</strong>
                </label>
                <input
                  id="captureRate"
                  type="range"
                  min={1}
                  max={15}
                  value={captureRate}
                  onChange={(e) => setCaptureRate(parseInt(e.target.value))}
                  className="calculator-slider"
                />
                <div className="slider-labels">
                  <span>1%</span>
                  <span>Typical range: 3–10%</span>
                  <span>15%</span>
                </div>
              </div>
            </div>

            <div className="calculator-results">
              <div className="calculator-result-row">
                <span className="result-label">Contacts captured per month</span>
                <span className="result-value">{formatNumber(contactsPerMonth)}</span>
              </div>
              <div className="calculator-result-row">
                <span className="result-label">Contacts captured per year</span>
                <span className="result-value">{formatNumber(contactsPerYear)}</span>
              </div>
              <div className="calculator-divider"></div>
              <div className="calculator-result-row estimate-row">
                <span className="result-label">Estimated annual revenue asset <span className="estimate-tag">Low</span></span>
                <span className="result-value result-highlight">{formatCurrency(lowEstimate)}</span>
              </div>
              <div className="calculator-result-row estimate-row">
                <span className="result-label">Estimated annual revenue asset <span className="estimate-tag high">High</span></span>
                <span className="result-value result-highlight high">{formatCurrency(highEstimate)}</span>
              </div>
              <p className="calculator-note">
                Based on {formatNumber(contactsPerYear)} contacts × $30–$80 per contact per year
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaway */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="feature-blocks">
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>The Opportunity Is Already in Your Restaurant</h3>
                <p>
                  Every guest whose contact information is not captured represents lost future revenue. 
                  Restaurants already have the traffic — thousands of guests per month sitting at tables, 
                  engaged and in a positive state of mind. The opportunity is capturing that relationship 
                  before they walk out the door.
                </p>
                <p>
                  TableChat enables guests to instantly engage, view promotions, and optionally provide 
                  their contact information during the visit — creating long-term revenue assets from 
                  existing traffic, with no additional marketing spend required.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Why TableChat Captures More Contacts</h4>
                <ul>
                  <li>Guests are already engaged in the chat — the opt-in is frictionless</li>
                  <li>No server pitch, no printed cards, no post-visit emails</li>
                  <li>Verified contact via one-time code — real data, not junk</li>
                  <li>Consent-based — guests choose to share, building trust</li>
                  <li>Works every table, every shift, every day — automatically</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="feature-stats data-sources-section">
        <div className="container">
          <h3 className="sources-heading">Sources</h3>
          <div className="sources-grid">
            <div className="source-card">
              <strong>Deliverect Restaurant Loyalty Report</strong>
              <p>Loyalty program members generate 12–18% more incremental annual revenue. Customers in loyalty programs spend up to 24% more per visit.</p>
            </div>
            <div className="source-card">
              <strong>SevenRooms Hospitality Report</strong>
              <p>95% of first-time diners never return without direct engagement. 83% of diners are willing to sign up for restaurant marketing programs.</p>
            </div>
            <div className="source-card">
              <strong>Olo Restaurant Engagement Data</strong>
              <p>Email marketing generates $36 for every $1 spent. Email is 40x more effective at converting first-time guests to regulars than social media.</p>
            </div>
            <div className="source-card">
              <strong>Emarsys SMS Marketing Benchmarks</strong>
              <p>SMS achieves 98% open rates vs. 37–42% for email. SMS campaigns generate an average return of $55 for every $1 spent.</p>
            </div>
            <div className="source-card">
              <strong>Hootsuite &amp; RivalIQ Social Media Reports</strong>
              <p>Organic social media posts reach only 2–6% of followers. Algorithm-driven platforms do not guarantee visibility for restaurant content.</p>
            </div>
            <div className="source-card">
              <strong>Campaign Monitor &amp; Postscript</strong>
              <p>Email subscriber annual value benchmarks for restaurants. SMS subscriber lifetime value ranges from $25 to $553 depending on engagement tier.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Every Empty Contact List Is a Revenue Leak</h2>
          <p>Your guests are already in your restaurant. Start capturing the relationship today.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default TheData;
